import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/app/Header";
import { api, getToken } from "@/lib/api";
import { ArrowLeft, TrendingUp, DollarSign, Store, ShieldAlert, Award } from "lucide-react";

export default function ScanReport() {
  const { id } = useParams();
  const [scan, setScan] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/scans/${id}`);
        setScan(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load scan");
      }
    })();
  }, [id]);

  if (error)
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center text-zinc-400">
          {error} · <Link to="/dashboard" className="text-[#10b981]">back to dashboard</Link>
        </div>
      </div>
    );

  if (!scan)
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center text-zinc-400">Loading report…</div>
      </div>
    );

  const r = scan.report || {};
  const token = getToken();
  const verdictColor =
    r.verdict?.decision === "BUY" ? "#00ff66" : r.verdict?.decision === "PASS" ? "#ff3b30" : "#ffb800";

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-[#10b981] mb-8"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        {/* Bento Grid */}
        <div className="grid grid-cols-12 gap-4">
          {/* Item ID */}
          <div className="col-span-12 md:col-span-5 border border-white/8 bg-[#0f1512]" data-testid="report-item">
            <div className="aspect-square bg-black overflow-hidden">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${scan.image_url}?auth=${token}`}
                alt={r.item_name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-6 border-t border-white/8">
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">/ Identified</p>
              <h1 className="font-display font-bold text-3xl tracking-tight">{r.item_name || "Item"}</h1>
              <div className="flex flex-wrap gap-2 mt-4">
                <Chip label={r.brand || "Unknown"} />
                <Chip label={r.category || "—"} />
                <Chip label={r.condition || "—"} />
              </div>
            </div>
          </div>

          {/* Value + verdict */}
          <div className="col-span-12 md:col-span-7 grid grid-cols-2 gap-4">
            <div className="col-span-2 border border-white/8 bg-[#0f1512] p-8" data-testid="report-value">
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">/ Estimated value</p>
              <div className="font-mono font-bold text-6xl text-[#10b981] tracking-tighter">
                ${r.estimated_value?.low ?? 0}–${r.estimated_value?.high ?? 0}
              </div>
              <p className="text-sm text-zinc-500 mt-2 font-mono uppercase tracking-widest">USD · Secondhand Market</p>
            </div>

            <div className="border border-white/8 bg-[#0f1512] p-6">
              <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">
                <DollarSign className="w-3 h-3" /> Suggested offer
              </div>
              <div className="font-mono font-bold text-4xl">${r.suggested_offer ?? 0}</div>
              <p className="text-xs text-zinc-500 mt-1">Max to pay</p>
            </div>

            <div className="border border-white/8 bg-[#0f1512] p-6">
              <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">
                <TrendingUp className="w-3 h-3" /> Est. Profit
              </div>
              <div className="font-mono font-bold text-4xl text-[#00ff66]">${r.profit_potential ?? 0}</div>
              <p className="text-xs text-zinc-500 mt-1">If resold @ ${r.resell_price ?? 0}</p>
            </div>

            <div
              className="col-span-2 border p-6"
              style={{ borderColor: verdictColor, backgroundColor: `${verdictColor}0d` }}
              data-testid="report-verdict"
            >
              <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest mb-3" style={{ color: verdictColor }}>
                <Award className="w-3 h-3" /> Verdict
              </div>
              <div className="flex items-baseline gap-4 flex-wrap">
                <div className="font-display font-bold text-5xl tracking-tight" style={{ color: verdictColor }}>
                  {r.verdict?.decision || "MAYBE"}
                </div>
                <div className="font-mono text-sm text-zinc-400">
                  Confidence · {Math.round((r.verdict?.confidence ?? 0) * 100)}%
                </div>
              </div>
              <p className="mt-3 text-zinc-300 leading-relaxed">{r.verdict?.summary}</p>
            </div>
          </div>

          {/* Marketplace comparison */}
          <div className="col-span-12 md:col-span-7 border border-white/8 bg-[#0f1512] p-6" data-testid="report-marketplaces">
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-500 mb-6">
              <Store className="w-3 h-3" /> Marketplace comparison
            </div>
            <div className="space-y-3">
              {(r.marketplaces || []).map((m) => {
                const isBest = m.name === r.best_marketplace;
                const max = Math.max(...(r.marketplaces || []).map((x) => x.avg_price || 0), 1);
                const pct = ((m.avg_price || 0) / max) * 100;
                return (
                  <div key={m.name} className={`border p-4 ${isBest ? "border-[#10b981] bg-[#10b981]/5" : "border-white/8"}`}>
                    <div className="flex justify-between items-baseline">
                      <div className="flex items-center gap-2">
                        <span className="font-display font-bold text-lg tracking-tight">{m.name}</span>
                        {isBest && (
                          <span className="font-mono text-[10px] uppercase tracking-widest text-[#10b981] border border-[#10b981] px-1.5 py-0.5">
                            BEST
                          </span>
                        )}
                      </div>
                      <div className="font-mono font-bold text-xl">${m.avg_price ?? 0}</div>
                    </div>
                    <div className="mt-3 h-1 bg-white/5">
                      <div className="h-full bg-[#10b981]" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex flex-wrap gap-4 mt-3 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                      <span>Speed · <span className="text-zinc-300">{m.sell_speed}</span></span>
                      <span>Fit · <span className="text-zinc-300">{m.audience_fit}</span></span>
                      {m.notes && <span className="text-zinc-500">{m.notes}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Risks */}
          <div className="col-span-12 md:col-span-5 border border-white/8 bg-[#0f1512] p-6" data-testid="report-risks">
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-500 mb-6">
              <ShieldAlert className="w-3 h-3" /> Risk flags
            </div>
            <div className="space-y-3">
              {(r.risks || []).map((risk, i) => {
                const color = risk.level === "high" ? "#ff3b30" : risk.level === "medium" ? "#ffb800" : "#a1a1aa";
                return (
                  <div key={i} className="border-l-2 pl-4 py-1" style={{ borderColor: color }}>
                    <div className="flex justify-between items-baseline gap-4">
                      <div className="font-display font-bold tracking-tight">{risk.label}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color }}>
                        {risk.level}
                      </div>
                    </div>
                    <div className="text-sm text-zinc-400 mt-1">{risk.detail}</div>
                  </div>
                );
              })}
              {r.condition_notes && (
                <div className="border-t border-white/8 pt-4 mt-4">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Condition notes</p>
                  <p className="text-sm text-zinc-400">{r.condition_notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Chip({ label }) {
  return (
    <span className="border border-white/20 text-xs uppercase tracking-widest font-mono px-2.5 py-1 text-zinc-300">
      {label}
    </span>
  );
}
